import type { NavigateFunction } from 'react-router-dom';

import {
  clearAuthUrlParams,
  generateNonceStr,
  LOG_PREFIX,
  logError,
  logInfo,
  parseAuthUrlParams,
  redirectToDomain,
  redirectToLiveWithCode,
} from './url';

import type { IUserLoginRequest, IUserStoreInfo, UserInfoDTO } from '@/api/user/types';
import { fetchDomainUsed, fetchUpdateLogin, fetchUserLogin } from '@/api/user';
import { detectEnvironment } from '@/utils/env';
import weixinAuth from '@/utils/weixin/auth';
import weixinSDK from '@/utils/weixin/sdk';
import { PLATFORM } from '@/types/enums/common';
import { RegisterChannel, UserStatus } from '@/types/enums/user';
import {
  getOpenIdByPlatform,
  getPlatformKey,
  getUserInfoLocal,
  type ICommonParams,
  type IUserInfoParams,
  setCommonParamsLocal,
  setUserInfoLocal,
} from '@/utils/storage';
import { fetchWxConfig } from '@/api/data';

/** 错误页面路由 */
export const ERROR_PAGES = {
  NOT_MINI_ENV: '/not-permission/1',
  DOMAIN_CONFIG_ERROR: '/not-permission/2',
  NOT_WECHAT_ENV: '/not-permission/3',
  LOGIN_FAILED: '/not-permission/4',
} as const;

/** 域名配置 */
export interface DomainConfig {
  authDomain: string;
  liveDomain: string;
  entryDomain: string;
}

/** 域名类型 */
export type DomainType = 'ACCESS' | 'AUTH' | 'LIVE' | 'OTHER' | 'LOCAL';

/** URL 授权参数 */
export interface AuthUrlParams {
  token: string;
  commonParams: ICommonParams | null;
  userInfo: Omit<IUserInfoParams, 'token'> | null;
}

/** URL 参数（未注入域名/搜索参数） */
export interface RouteParams {
  code: string;
  platform: string;
  appId: string;
  storeId: string;
  openId: string;
  unionId: string;
  skipWxAuth: boolean;
  isPopup: boolean;
  needWxAuth: boolean;
  urlAuthDomain: string;
  urlLiveDomain: string;
  urlEntryDomain: string;
}

/** 授权上下文（已注入域名/搜索参数） */
export interface AuthContext extends RouteParams {
  domains: DomainConfig;
  searchParams: URLSearchParams;
}

/** 授权重试计数（避免循环） */
const AUTH_REQUIRED_RETRY_KEY = 'BELINK-AUTH-REQUIRED-RETRY';
const AUTH_FLOW_RETRY_KEY = 'BELINK-WXAUTH-FLOW-RETRY';

/** 获取授权重试次数（sessionStorage） */
function getAuthRequiredRetryCount(): number {
  try {
    return Number(sessionStorage.getItem(AUTH_REQUIRED_RETRY_KEY) || '0');
  } catch {
    return 0;
  }
}

/** 设置授权重试次数（sessionStorage） */
function setAuthRequiredRetryCount(count: number): void {
  try {
    sessionStorage.setItem(AUTH_REQUIRED_RETRY_KEY, String(count));
  } catch {
    // ignore storage errors
  }
}

/** 清理授权重试次数 */
function clearAuthRequiredRetryCount(): void {
  try {
    sessionStorage.removeItem(AUTH_REQUIRED_RETRY_KEY);
  } catch {
    // ignore storage errors
  }
}

/** 获取授权流程重试次数（B 域名 authorize） */
function getAuthFlowRetryCount(): number {
  try {
    return Number(sessionStorage.getItem(AUTH_FLOW_RETRY_KEY) || '0');
  } catch {
    return 0;
  }
}

/** 设置授权流程重试次数 */
function setAuthFlowRetryCount(count: number): void {
  try {
    sessionStorage.setItem(AUTH_FLOW_RETRY_KEY, String(count));
  } catch {
    // ignore storage errors
  }
}

/** 清理授权流程重试次数 */
function clearAuthFlowRetryCount(): void {
  try {
    sessionStorage.removeItem(AUTH_FLOW_RETRY_KEY);
  } catch {
    // ignore storage errors
  }
}

function getSnapshotParamsFromLocal(): { snapshotUserId: string; snapshotToken: string } {
  const storedUserInfo = getUserInfoLocal();
  if (storedUserInfo && isLocalSnapshotUser(storedUserInfo)) {
    return {
      snapshotUserId: storedUserInfo.userId || '',
      snapshotToken: storedUserInfo.token || '',
    };
  }
  return { snapshotUserId: '', snapshotToken: '' };
}

function getSnapshotParamsFromUrl(searchParams: URLSearchParams): { snapshotUserId: string; snapshotToken: string } {
  return {
    snapshotUserId: searchParams.get('snapshotUserId') || '',
    snapshotToken: searchParams.get('token') || '',
  };
}

/** 提取域名的 hostname */
function extractHostname(domain: string): string {
  try {
    if (domain.startsWith('http://') || domain.startsWith('https://')) {
      return new URL(domain).hostname;
    }
    return domain;
  } catch {
    return domain;
  }
}

/** 检测当前域名类型 */
export function detectDomainType(domains: DomainConfig): DomainType {
  const env = detectEnvironment();
  const currentHost = window.location.hostname;
  const authHost = extractHostname(domains.authDomain);
  const liveHost = extractHostname(domains.liveDomain);
  const entryHost = extractHostname(domains.entryDomain);

  if (env.isDev) return 'LOCAL';
  if (currentHost === authHost) return 'AUTH';
  if (currentHost === liveHost) return 'LIVE';
  if (currentHost === entryHost) return 'ACCESS';
  return 'OTHER';
}

/** 获取域名配置（URL 参数优先，其次接口） */
export async function resolveDomainConfig(
  urlParams: RouteParams,
  navigate: NavigateFunction,
): Promise<DomainConfig | null> {
  const { urlAuthDomain, urlLiveDomain, urlEntryDomain } = urlParams;

  if (urlAuthDomain && urlLiveDomain && urlEntryDomain) {
    logInfo(`${LOG_PREFIX} 使用 URL 域名配置`);
    return { authDomain: urlAuthDomain, liveDomain: urlLiveDomain, entryDomain: urlEntryDomain };
  }

  try {
    logInfo(`${LOG_PREFIX} 获取域名配置`);
    const res = await fetchDomainUsed();
    const { used } = res.data;

    if (!used?.[1] || !used?.[2] || !used?.[3]) {
      logError(`${LOG_PREFIX} 域名配置不完整`);
      navigate(ERROR_PAGES.DOMAIN_CONFIG_ERROR, { replace: true });
      return null;
    }

    return { authDomain: used[1], liveDomain: used[2], entryDomain: used[3] };
  } catch (error) {
    logError(`${LOG_PREFIX} 获取域名配置失败:`, error);
    navigate(ERROR_PAGES.DOMAIN_CONFIG_ERROR, { replace: true });
    return null;
  }
}

/** URL 带 noJump=true 时跳过跨域名 location.replace，在当前域名继续流程 */
export function isNoJumpMode(searchParams: URLSearchParams): boolean {
  return searchParams.get('noJump') === 'true';
}

/** 解析路由参数 */
export function parseRouteParams(searchParams: URLSearchParams): RouteParams {
  return {
    code: searchParams.get('code') || '',
    platform: searchParams.get('platform') || PLATFORM.H5,
    appId: searchParams.get('appId') || import.meta.env.VITE_WECHAT_MP_APPID || '',
    storeId: searchParams.get('storeId') || '',
    openId: searchParams.get('openId') || '',
    unionId: searchParams.get('unionId') || '',
    skipWxAuth: searchParams.get('skipWxAuth') === 'true',
    isPopup: searchParams.get('isPopup') === 'true',
    needWxAuth: searchParams.get('needWxAuth') === 'true',
    urlAuthDomain: searchParams.get('authDomain') || '',
    urlLiveDomain: searchParams.get('liveDomain') || '',
    urlEntryDomain: searchParams.get('entryDomain') || '',
  };
}

/** 根据平台获取注册渠道 */
function getRegisterChannel(platform: string): RegisterChannel {
  switch (platform) {
    case PLATFORM.APP:
      return RegisterChannel.HEALTH_APP;
    case PLATFORM.MINI:
      return RegisterChannel.BE_LINK_MINI_PROGRAM;
    default:
      return RegisterChannel.WECHAT_WEB_AUTH;
  }
}

/** 构建公共参数 */
function buildCommonParams(appId: string, platform: string, domains: DomainConfig | null): ICommonParams {
  return {
    appId,
    platform,
    entryDomain: domains?.entryDomain || '',
    authDomain: domains?.authDomain || '',
    liveDomain: domains?.liveDomain || '',
    fontSizeSetting: '16',
  };
}

/** 构建用户信息参数 */
function buildUserInfoParams(
  userInfo: UserInfoDTO,
  storeInfo: IUserStoreInfo | undefined,
  platform: string,
  urlOpenId?: string,
  appId?: string,
): Omit<IUserInfoParams, 'token'> {
  const storedUserInfo = getUserInfoLocal();
  const currentOpenId = urlOpenId || userInfo.openId;
  const platformKey = getPlatformKey(platform);

  return {
    userId: userInfo.id,
    unionId: userInfo.unionId,
    openId: currentOpenId,
    nickname: userInfo.nickname || '',
    avatar: userInfo.avatar || '',
    userStoreId: userInfo.storeId || '',
    userStoreName: userInfo.storeName || '',
    managerStoreId: storeInfo?.storeId || '',
    managerStoreName: storeInfo?.storeName || '',
    isBlocked: userInfo.status === UserStatus.BLOCKED,
    isNewUser: !userInfo.storeId,
    h5: storedUserInfo?.h5,
    mini: storedUserInfo?.mini,
    app: storedUserInfo?.app,
    [platformKey]: {
      appId: appId || '',
      openId: currentOpenId,
    },
  };
}

/** 登录结果类型 */
export type LoginOutcome = 'authorized' | 'need_auth' | 'snapshot';

/**
 * 快照用户判定：
 * - 接口明确返回 isSnapShotUser=true
 * - 或昵称为“微信用户”且 isSnapShotUser 未返回/为 true
 */
function isSnapshotUser(userInfo: UserInfoDTO, isSnapShotUser?: boolean): boolean {
  if (isSnapShotUser === true) return true;
  if (userInfo.nickname === '微信用户' && isSnapShotUser === undefined) return true;
  return false;
}

function resolveSnapshotFlagForStore(userInfo: UserInfoDTO, isSnapShotUser?: boolean): boolean {
  if (isSnapShotUser !== undefined) return Boolean(isSnapShotUser);
  return userInfo.nickname === '微信用户';
}

/** 登录并落库（包含 authRequired / snapshot 处理） */
async function executeLoginAndStore(
  loginParams: IUserLoginRequest,
  commonParams: ICommonParams,
  platform: string,
  urlOpenId?: string,
  appId?: string,
): Promise<LoginOutcome> {
  const res = await fetchUserLogin(loginParams);
  const { userInfo, token, storeInfo, isSnapShotUser, authRequired, isStoreManager } = res.data;
  const snapshotUser = isSnapshotUser(userInfo, isSnapShotUser);
  const snapshotFlag = resolveSnapshotFlagForStore(userInfo, isSnapShotUser);

  if (authRequired && !snapshotUser) {
    const retryCount = getAuthRequiredRetryCount();
    if (retryCount >= 1) {
      logInfo(`${LOG_PREFIX} 授权后仍返回需要授权，继续使用登录信息`);
      clearAuthRequiredRetryCount();
    } else {
      logInfo(`${LOG_PREFIX} 登录返回需要授权`);
      setAuthRequiredRetryCount(1);
      return 'need_auth';
    }
  }

  const userInfoObj = buildUserInfoParams(userInfo, storeInfo, platform, urlOpenId, appId);
  setCommonParamsLocal(commonParams);
  setUserInfoLocal({
    token,
    ...userInfoObj,
    isSnapShotUser: snapshotFlag,
    isStoreManager: isStoreManager ?? false,
    thirdPartyAuthRecord: res.data?.thirdPartyAuthRecord,
  });
  clearAuthRequiredRetryCount();

  if (snapshotUser) {
    logInfo(`${LOG_PREFIX} 登录返回快照用户，等待手动授权`);
    return 'snapshot';
  }

  logInfo(`${LOG_PREFIX} 登录成功，用户ID: ${userInfo.id}`);
  return 'authorized';
}

/** 登录并落库（忽略 authRequired，用于 update-login 二次登录） */
async function executeLoginAndStoreIgnoringAuthRequired(
  loginParams: IUserLoginRequest,
  commonParams: ICommonParams,
  platform: string,
  urlOpenId?: string,
  appId?: string,
): Promise<LoginOutcome> {
  const res = await fetchUserLogin(loginParams);
  const { userInfo, token, storeInfo, isSnapShotUser, thirdPartyAuthRecord, isStoreManager } = res.data;
  const snapshotUser = isSnapshotUser(userInfo, isSnapShotUser);
  const snapshotFlag = resolveSnapshotFlagForStore(userInfo, isSnapShotUser);
  const userInfoObj = buildUserInfoParams(userInfo, storeInfo, platform, urlOpenId, appId);

  setCommonParamsLocal(commonParams);
  setUserInfoLocal({
    token,
    ...userInfoObj,
    isSnapShotUser: snapshotFlag,
    isStoreManager: isStoreManager ?? false,
    thirdPartyAuthRecord,
  });
  clearAuthRequiredRetryCount();

  if (snapshotUser) {
    logInfo(`${LOG_PREFIX} 登录返回快照用户，等待手动授权`);
    return 'snapshot';
  }

  logInfo(`${LOG_PREFIX} 登录成功，用户ID: ${userInfo.id}`);
  return 'authorized';
}

/** 直接登录（unionId/openId） */
export async function loginDirect(context: AuthContext): Promise<LoginOutcome> {
  const { appId, openId, unionId, platform, domains, storeId } = context;
  logInfo(`${LOG_PREFIX} [${platform}] 直接登录`);

  const storedUserInfo = getUserInfoLocal();
  const isLocalSnapshot = isLocalSnapshotUser(storedUserInfo);
  if (isLocalSnapshot) {
    logInfo(`${LOG_PREFIX} [${platform}] 快照用户，跳转授权`);
    return 'need_auth';
  }
  const urlOpenId = openId;
  // H5 平台必须有专属 openId，不能 fallback 到其他平台（如小程序）的 openId
  // 否则会导致 appId（服务号）与 openId（小程序）不匹配
  const platformOpenId = urlOpenId || getOpenIdByPlatform(storedUserInfo, platform) || '';

  if (platform === PLATFORM.H5 && !platformOpenId) {
    logInfo(`${LOG_PREFIX} [H5] 没有 openId，需要走微信授权`);
    return 'need_auth';
  }

  const finalOpenId = platformOpenId || storedUserInfo?.openId || '';
  const finalUnionId = unionId || storedUserInfo?.unionId || '';

  if ((platform === PLATFORM.APP || platform === PLATFORM.MINI) && (!appId || !finalOpenId || !finalUnionId)) {
    logError(`${LOG_PREFIX} 缺少必要参数`);
    throw new Error('LOGIN_FAILED');
  }

  const loginParams: IUserLoginRequest = {
    appId,
    openId: finalOpenId,
    unionId: finalUnionId,
    storeId,
    registerChannel: getRegisterChannel(platform),
  };

  const commonParams = buildCommonParams(appId, platform, domains);
  if (platform === PLATFORM.APP) {
    // APP webview 无法走 H5 微信授权回跳，authRequired 时也需要保留登录返回的用户信息。
    return executeLoginAndStoreIgnoringAuthRequired(loginParams, commonParams, platform, urlOpenId, appId);
  }
  return executeLoginAndStore(loginParams, commonParams, platform, urlOpenId, appId);
}

/** code 登录（H5 公众号授权回调） */
export async function loginWithCode(context: AuthContext): Promise<LoginOutcome> {
  const { code, appId, platform, storeId, domains, openId } = context;

  const loginParams: IUserLoginRequest = {
    appId,
    storeId,
    webAuthCode: code,
    registerChannel: RegisterChannel.WECHAT_WEB_AUTH,
  };

  const commonParams = buildCommonParams(appId, platform, domains);
  // 传递 URL 中的 openId，确保小程序等场景下使用正确的平台 openId
  return executeLoginAndStore(loginParams, commonParams, platform, openId, appId);
}

/** URL 带 token/userInfo 直接落库 */
export async function storeAuthFromUrl(context: AuthContext): Promise<void> {
  const { searchParams } = context;
  const { token, commonParams, userInfo } = parseAuthUrlParams(searchParams);
  const hasUrlAuth = token && userInfo?.userId;

  const storedUserInfo = getUserInfoLocal();
  const hasStoredAuth = storedUserInfo?.token && storedUserInfo?.userId;

  if (!hasUrlAuth && !hasStoredAuth) {
    logInfo(`${LOG_PREFIX} [C] 未授权，跳转到授权域名`);
    throw new Error('AUTH_REQUIRED');
  }

  if (commonParams) {
    setCommonParamsLocal(commonParams);
  }
  if (userInfo) {
    setUserInfoLocal({ token, ...userInfo });
  }
}

/** 快照用户授权后补全真实信息 */
export async function updateLoginAfterAuth(
  context: AuthContext,
  currentUserId: string,
): Promise<{ outcome: LoginOutcome; updatedUserId?: string }> {
  const { code, platform, appId, domains } = context;
  const res = await fetchUpdateLogin({ webAuthCode: code, userId: currentUserId });
  const updateInfo = res.data;

  if (!updateInfo?.userId || !updateInfo.openId || !updateInfo.unionId) {
    throw new Error('update-login 返回数据不完整');
  }

  const loginParams: IUserLoginRequest = {
    appId,
    openId: updateInfo.openId,
    unionId: updateInfo.unionId,
    registerChannel: getRegisterChannel(platform),
  };
  const commonParams = buildCommonParams(appId, platform, domains);
  const outcome = await executeLoginAndStoreIgnoringAuthRequired(
    loginParams,
    commonParams,
    platform,
    updateInfo.openId,
    appId,
  );
  return { outcome, updatedUserId: updateInfo.userId };
}

/** 跳转到授权域名（记录一次授权尝试） */
export function redirectToAuthDomain(domains: DomainConfig, searchParams: URLSearchParams): void {
  logInfo(`${LOG_PREFIX} 跳转到授权域名`);
  const { snapshotUserId, snapshotToken } = getSnapshotParamsFromLocal();
  const extraParams: Record<string, string> = {};
  if (snapshotUserId) extraParams.snapshotUserId = snapshotUserId;
  if (snapshotToken) extraParams.token = snapshotToken;
  redirectToDomain(domains.authDomain, searchParams, Object.keys(extraParams).length ? extraParams : undefined);
}

/** 跳转到直播域名 */
export function redirectToLiveDomain(domains: DomainConfig, searchParams: URLSearchParams): void {
  logInfo(`${LOG_PREFIX} 跳转到直播域名`);
  redirectToDomain(domains.liveDomain, searchParams);
}

/** 由 useShare 管理分享的页面路径 */
const SHARE_MANAGED_PATHS = ['/product-detail', '/shopping-festival', '/points-mall', '/scene', '/new-member-center'];
/** 可以浏览器打开的页面路径 */
const OPEN_BROWSER_MANAGED_PATHS = ['/download-guide', '/download-guide-2'];

/** 判断当前页面是否由 useShare 管理分享 */
function isShareManagedPage(): boolean {
  const pathname = window.location.pathname;
  return SHARE_MANAGED_PATHS.some((path) => pathname.startsWith(path));
}

/** 判断当前页面是否可以在浏览器打开 */
function isOpenBrowserManagedPage(): boolean {
  const pathname = window.location.pathname;
  return OPEN_BROWSER_MANAGED_PATHS.some((path) => pathname.startsWith(path));
}

/**
 * 初始化微信 JSSDK
 * type: 票据类型  VITALITY_PLUS_JSAPI_TICKET - PARTY_JSAPI_TICKET
 */
export async function initWeixinSDK(type: string): Promise<void> {
  try {
    const timestamp = Math.floor(Date.now() / 1000);
    const nonceStr = generateNonceStr();
    const url = window.location.href.split('#')[0];
    const res = await fetchWxConfig({ type, timestamp, nonceStr, url });
    await weixinSDK.config({
      appId: res.data.appId,
      timestamp: res.data.timestamp,
      nonceStr: res.data.nonceStr,
      signature: res.data.signature,
    });

    if (isOpenBrowserManagedPage()) {
      await weixinSDK.hideShareMenu({ showAppMessage: false, showBrowser: true });
    } else if (!isShareManagedPage()) {
      await weixinSDK.hideShareMenu({ showAppMessage: false, showBrowser: false });
      logInfo(`${LOG_PREFIX} 微信 JSSDK 初始化成功，非分享页面已隐藏分享菜单`);
    } else {
      logInfo(`${LOG_PREFIX} 微信 JSSDK 初始化成功，分享由 useShare hook 管理`);
    }
  } catch (error) {
    console.log('initWeixinSDK error', error);
    logError(`${LOG_PREFIX} 初始化微信 JSSDK 异常:`, error);
  }
}

/** 本地快照用户判定（基于缓存） */
export function isLocalSnapshotUser(storedUserInfo: IUserInfoParams | null): boolean {
  if (!storedUserInfo) return false;
  if (storedUserInfo.isSnapShotUser === true) return true;
  return storedUserInfo.nickname === '微信用户' && storedUserInfo.isSnapShotUser !== false;
}

/** 判断是否需要走 update-login */
export function shouldUpdateLogin(storedUserInfo: IUserInfoParams | null): boolean {
  return isLocalSnapshotUser(storedUserInfo);
}

/** C 域名动作类型（状态机） */
export type LiveAction =
  | { type: 'skip_auth' }
  | { type: 'code_login' }
  | { type: 'url_auth' }
  | { type: 'need_auth' }
  | { type: 'missing_union' }
  | { type: 'direct_login'; unionId: string };

/** 根据上下文与本地信息解析 C 域名动作 */
export function resolveLiveAction(
  context: AuthContext,
  urlAuth: AuthUrlParams,
  storedUserInfo: IUserInfoParams | null,
): LiveAction {
  if (context.skipWxAuth) return { type: 'skip_auth' };
  if (context.code) return { type: 'code_login' };
  if (urlAuth.token && urlAuth.userInfo?.userId) return { type: 'url_auth' };
  if (context.platform === PLATFORM.APP) return { type: 'skip_auth' };
  if (context.needWxAuth) return { type: 'need_auth' };

  const urlUnionId = context.searchParams.get('unionId') || '';
  const currentUnionId = context.unionId || urlUnionId || urlAuth.userInfo?.unionId || storedUserInfo?.unionId || '';
  if (!currentUnionId) return { type: 'missing_union' };

  return { type: 'direct_login', unionId: currentUnionId };
}

/** 按域名类型运行授权流程 */
export async function runDomainFlow(
  domainType: DomainType,
  context: AuthContext,
  setLoading: (loading: boolean) => void,
  navigate: NavigateFunction,
): Promise<void> {
  switch (domainType) {
    case 'LOCAL':
    case 'LIVE':
      await runLiveDomainFlow(context, setLoading, navigate);
      return;
    case 'AUTH':
      // B 域名：有 code 则回跳 C，没 code 则发起授权
      if (context.code) {
        const { code, domains, searchParams } = context;
        try {
          clearAuthFlowRetryCount();
          const { snapshotUserId, snapshotToken } = getSnapshotParamsFromUrl(searchParams);
          const extraParams: Record<string, string> = {};
          if (snapshotUserId) extraParams.snapshotUserId = snapshotUserId;
          if (snapshotToken) extraParams.token = snapshotToken;
          redirectToLiveWithCode(
            domains.liveDomain,
            searchParams,
            code,
            Object.keys(extraParams).length ? extraParams : undefined,
          );
        } catch (error) {
          logError(`${LOG_PREFIX} [B] 回跳失败:`, error);
          navigate(ERROR_PAGES.LOGIN_FAILED, { replace: true });
        }
      } else {
        const retryCount = getAuthFlowRetryCount();
        if (retryCount >= 1) {
          logError(`${LOG_PREFIX} [B] 授权失败或被拒绝，停止重试`);
          navigate(ERROR_PAGES.LOGIN_FAILED, { replace: true });
          return;
        }
        setAuthFlowRetryCount(retryCount + 1);
        weixinAuth.authorize(window.location.href, 'snsapi_userinfo');
      }
      return;
    case 'ACCESS':
    case 'OTHER':
    default:
      if (isNoJumpMode(context.searchParams)) {
        logInfo(`${LOG_PREFIX} noJump=true，跳过 C 域名重定向，在当前域名继续授权流程`);
        await runLiveDomainFlow(context, setLoading, navigate);
        return;
      }
      logInfo(`${LOG_PREFIX} 跳转到 C 域名检查授权状态`);
      redirectToLiveDomain(context.domains, context.searchParams);
      return;
  }
}

/** C 域名主流程 */
export async function runLiveDomainFlow(
  context: AuthContext,
  setLoading: (loading: boolean) => void,
  navigate: NavigateFunction,
): Promise<void> {
  const { searchParams } = context;
  const urlAuth = parseAuthUrlParams(searchParams);
  const storedUserInfo = getUserInfoLocal();
  const action = resolveLiveAction(context, urlAuth, storedUserInfo);
  await executeLiveAction(action, context, urlAuth, storedUserInfo, setLoading, navigate);
}

/** 执行 C 域名动作 */
async function executeLiveAction(
  action: LiveAction,
  context: AuthContext,
  urlAuth: AuthUrlParams,
  storedUserInfo: ReturnType<typeof getUserInfoLocal>,
  setLoading: (loading: boolean) => void,
  navigate: NavigateFunction,
): Promise<void> {
  const { domains, searchParams, platform } = context;
  switch (action.type) {
    case 'skip_auth': {
      logInfo(`${LOG_PREFIX} [C] ${platform === PLATFORM.APP ? 'APP 平台跳过授权' : 'skipWxAuth=true'}，直接登录`);
      try {
        const outcome = await loginDirect(context);
        if (outcome === 'need_auth') {
          logInfo(`${LOG_PREFIX} [C] skipWxAuth 下缺少必要参数，放行页面`);
          setLoading(false);
          return;
        }
        await applyOutcome(outcome, context, setLoading);
        return;
      } catch (error) {
        logError(`${LOG_PREFIX} [C] skipWxAuth 直接登录失败:`, error);
        setLoading(false);
        return;
      }
    }
    case 'code_login': {
      try {
        const { snapshotUserId, snapshotToken } = getSnapshotParamsFromUrl(searchParams);
        if (snapshotUserId && snapshotToken) {
          const { outcome } = await updateLoginAfterAuth(context, snapshotUserId);
          await applyOutcome(outcome, context, setLoading);
          return;
        }
        const outcome = await loginWithCode(context);
        await applyOutcome(outcome, context, setLoading);
        return;
      } catch (error) {
        logError(`${LOG_PREFIX} [C] code 登录失败:`, error);
        navigate(ERROR_PAGES.LOGIN_FAILED, { replace: true });
        return;
      }
    }
    case 'url_auth': {
      // iOS 对 JSSDK 签名 URL 很敏感，必须在清理 URL 前初始化
      if (!detectEnvironment().isDev && platform === PLATFORM.H5) {
        await initWeixinSDK('VITALITY_PLUS_JSAPI_TICKET');
      }
      try {
        await storeAuthFromUrl(context);
        clearAuthUrlParams();
        clearAuthFlowRetryCount();
        setLoading(false);
        return;
      } catch {
        if (storedUserInfo && shouldUpdateLogin(storedUserInfo)) {
          logError(`${LOG_PREFIX} [C] URL 授权参数异常，继续使用快照信息`);
          clearAuthUrlParams();
          setLoading(false);
          return;
        }
        clearAuthUrlParams();
        logError(`${LOG_PREFIX} [C] URL 授权参数异常，跳转到错误页`);
        navigate(ERROR_PAGES.LOGIN_FAILED, { replace: true });
        return;
      }
    }
    case 'need_auth': {
      logInfo(`${LOG_PREFIX} [C] needWxAuth=true，跳转到授权域名`);
      if (isNoJumpMode(searchParams)) {
        logInfo(`${LOG_PREFIX} [C] noJump=true，跳过授权域名重定向`);
        setLoading(false);
        return;
      }
      redirectToAuthDomain(domains, searchParams);
      return;
    }
    case 'missing_union': {
      logInfo(`${LOG_PREFIX} [C] 未授权，跳转到授权域名`);
      if (isNoJumpMode(searchParams)) {
        logInfo(`${LOG_PREFIX} [C] noJump=true，跳过授权域名重定向`);
        setLoading(false);
        return;
      }
      redirectToAuthDomain(domains, searchParams);
      return;
    }
    case 'direct_login': {
      try {
        const outcome = await loginDirect({ ...context, unionId: action.unionId });
        await applyOutcome(outcome, context, setLoading);
      } catch (error) {
        logError(`${LOG_PREFIX} [C] 直接登录失败:`, error);
        navigate(ERROR_PAGES.LOGIN_FAILED, { replace: true });
      }
      return;
    }
  }
}

/** 环境检查（非微信环境拦截） */
export function checkEnvironment(platform: string, skipWxAuth: boolean): string | null {
  const env = detectEnvironment();

  if (skipWxAuth || env.isDev) {
    return null;
  }

  if (platform === PLATFORM.MINI && !env.isWeChatMini) {
    logInfo(`${LOG_PREFIX} 非微信小程序环境`);
    return ERROR_PAGES.NOT_MINI_ENV;
  }

  if (platform === PLATFORM.H5 && !env.isWeChat) {
    logInfo(`${LOG_PREFIX} 非微信环境`);
    return ERROR_PAGES.NOT_WECHAT_ENV;
  }

  return null;
}

/** 根据登录结果执行收尾动作 */
export async function applyOutcome(
  outcome: LoginOutcome,
  context: AuthContext,
  setLoading: (loading: boolean) => void,
): Promise<void> {
  const { domains, searchParams, platform } = context;

  switch (outcome) {
    case 'authorized':
      // iOS 对 JSSDK 签名 URL 很敏感，必须在清理 URL 前初始化
      if (!detectEnvironment().isDev && platform === PLATFORM.H5) {
        await initWeixinSDK('VITALITY_PLUS_JSAPI_TICKET');
      }
      clearAuthUrlParams();
      setLoading(false);
      return;
    case 'snapshot':
      clearAuthUrlParams();
      setLoading(false);
      return;
    case 'need_auth':
    default:
      if (isNoJumpMode(searchParams)) {
        logInfo(`${LOG_PREFIX} noJump=true，跳过授权域名重定向`);
        setLoading(false);
        return;
      }
      redirectToAuthDomain(domains, searchParams);
      return;
  }
}

/** 是否弹窗模式（跳过授权） */
export function isPopupMode(params: RouteParams): boolean {
  return params.isPopup;
}

export { LOG_PREFIX, logError, logInfo };
